import { type ChatMessage, ChatRoom, SendMessageRequest } from 'amazon-ivs-chat-messaging'
import { create } from 'zustand'

import { sendPrivateLiveChatMessage } from 'services/endpoints/streaming'

type UIMessage = ChatMessage & {
  avatar_url?: string
  username?: string
  to_user?: string | number | null
  isOwner?: boolean
}

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected'
type MessagesByStream = Record<string, UIMessage[]>
type HistoryLoadedByStream = Record<string, boolean>

type TokenProvider = () => Promise<{
  token: string
  sessionExpirationTime?: Date | string
  tokenExpirationTime?: Date | string
}>

export type ChatHistoryItem = {
  message_id?: string | number | null
  id?: string | number | null
  content?: string | null
  user_id?: string | number | null
  username?: string | null
  avatar_url?: string | null
  to_user?: string | number | null
  ts?: number | null
  sent_at?: string | null
  created_at?: string | null
}

type ChatUserData = {
  id?: string | number
  username?: string
  avatar?: { url?: string }
  cropped_avatar?: Array<{ url?: string }>
  profile_photo_url?: string
}

type ConnectArgs = {
  region?: string
  tokenProvider: TokenProvider
  archiver?: boolean
  streamId?: string | number | null
  streamOwnerId?: string | number | null
  userData?: ChatUserData
  devTap?: (payload: {
    roomArn: string
    messageId: string | null
    userId: string | number
    username: string | null
    content: string
    ts: number
    attrs: Record<string, unknown>
    avatar_url?: string
    to_user?: string | number | null
  }) => Promise<any>
  roomArn?: string
}

type SendMessageOptions = {
  toUser?: string | number | null
}

type IvsChatStore = {
  room: ChatRoom | null
  connection: ConnectionState
  activeStreamId: string | null
  streamOwnerId: string | null
  messagesByStream: MessagesByStream
  historyLoadedByStream: HistoryLoadedByStream
  isSending: boolean
  userData?: ChatUserData

  connectChat: (args: ConnectArgs) => Promise<void>
  disconnectChat: () => void
  sendMessage: (content: string, options?: SendMessageOptions) => Promise<void>

  clearMessagesForStream: (streamId: string | number) => void
  getMessagesForStream: (streamId: string | number | null | undefined) => UIMessage[]

  markHistoryLoadedForStream: (streamId: string | number) => void
  clearHistoryLoadedForStream: (streamId: string | number) => void
  hasHistoryLoadedForStream: (streamId: string | number | null | undefined) => boolean

  prependHistoryForStream: (
    streamId: string | number,
    rows: ChatHistoryItem[],
    streamOwnerId?: string | number | null
  ) => void
}

const tappedIds = new Set<string>()

const getAvatarUrl = (userData?: ChatUserData): string => {
  return (
    userData?.avatar?.url ||
    userData?.cropped_avatar?.[0]?.url ||
    userData?.profile_photo_url ||
    ''
  )
}

const safeParseObject = (value: unknown): Record<string, unknown> => {
  if (!value) return {}

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>
      }
      return {}
    } catch {
      return {}
    }
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }

  return {}
}

const extractChatAttrs = (msg: ChatMessage): Record<string, any> => {
  try {
    // CASE 1: msg.attributes.attributes (your exact case)
    const nested = (msg as any)?.attributes?.attributes
    if (typeof nested === 'string') {
      return JSON.parse(nested)
    }

    // CASE 2: msg.attributes is string
    const raw = (msg as any)?.attributes
    if (typeof raw === 'string') {
      return JSON.parse(raw)
    }

    // CASE 3: msg.attributes is object already
    if (typeof raw === 'object' && raw) {
      return raw
    }

    // CASE 4: sender.attributes fallback
    const senderAttrs = (msg.sender as any)?.attributes
    if (typeof senderAttrs === 'string') {
      return JSON.parse(senderAttrs)
    }
    if (typeof senderAttrs === 'object' && senderAttrs) {
      return senderAttrs
    }
  } catch (e) {
    console.error('[IVS ATTR PARSE ERROR]', e)
  }

  return {}
}

const normalizeHistoryMessage = (
  row: ChatHistoryItem,
  streamOwnerId?: string | number | null
): UIMessage => {
  const parsedTime =
    typeof row?.ts === 'number'
      ? new Date(row.ts)
      : new Date(Date.parse(row?.sent_at ?? row?.created_at ?? ''))

  const safeTime = Number.isNaN(parsedTime.getTime()) ? new Date() : parsedTime

  const resolvedId =
    row?.message_id != null
      ? String(row.message_id)
      : row?.id != null
        ? String(row.id)
        : `hist-${String(row?.user_id ?? 'unknown')}-${String(
            row?.created_at ?? row?.sent_at ?? Date.now()
          )}`

  return {
    id: resolvedId,
    content: row?.content ?? '',
    sender: {
      userId: String(row?.user_id ?? 'unknown'),
    },
    username: row?.username ?? undefined,
    avatar_url: row?.avatar_url ?? undefined,
    to_user: row?.to_user ?? null,
    sendTime: safeTime,
    isOwner: String(row?.user_id ?? '') === String(streamOwnerId ?? ''),
  } satisfies UIMessage
}

const teardownRoom = (room: ChatRoom | null) => {
  if (!room) return

  try {
    const unsubs: Array<() => void> = (room as any).__unsubs ?? []
    unsubs.forEach(fn => {
      try {
        fn()
      } catch {}
    })
  } catch {}

  try {
    delete (room as any).__unsubs
  } catch {}

  try {
    room.disconnect()
  } catch {}
}

export const useIvsChatStore = create<IvsChatStore>((set, get) => ({
  room: null,
  connection: 'idle',
  activeStreamId: null,
  streamOwnerId: null,
  messagesByStream: {},
  historyLoadedByStream: {},
  isSending: false,
  userData: undefined,

  connectChat: async ({
    region,
    tokenProvider,
    archiver = false,
    userData,
    devTap,
    roomArn,
    streamId,
    streamOwnerId,
  }: ConnectArgs) => {
    const nextStreamKey = String(streamId ?? '')
    const existingRoom = get().room
    const currentStreamKey = String(get().activeStreamId ?? '')
    const currentConnection = get().connection

    if (
      existingRoom &&
      currentStreamKey === nextStreamKey &&
      (currentConnection === 'connected' || currentConnection === 'connecting')
    ) {
      set({
        userData,
        streamOwnerId: streamOwnerId != null ? String(streamOwnerId) : null,
      })
      return
    }

    if (existingRoom) {
      teardownRoom(existingRoom)
      set({
        room: null,
        connection: 'idle',
      })
    }

    set({
      connection: 'connecting',
      activeStreamId: nextStreamKey || null,
      streamOwnerId: streamOwnerId != null ? String(streamOwnerId) : null,
      userData,
    })

    const regionOrUrl =
      region ?? (window as any).__IVS_CHAT__?.region ?? 'us-west-2'

    const resolvedProvider: TokenProvider =
      tokenProvider ??
      (window as any).__IVS_CHAT__?.tokenProvider ??
      (async () => {
        throw new Error('IVS chat token provider not configured')
      })

    const room = new ChatRoom({
      regionOrUrl,
      tokenProvider: async () => {
        const tok = await resolvedProvider()
        const normalize = (v?: Date | string) =>
          typeof v === 'string' ? new Date(v) : v

        return {
          token: tok.token,
          sessionExpirationTime: normalize(tok.sessionExpirationTime),
          tokenExpirationTime: normalize(tok.tokenExpirationTime),
        }
      },
    })

    const unsubs: Array<() => void> = []

    unsubs.push(
      room.addListener('connecting', () => {
        set({ connection: 'connecting' })
      })
    )

    unsubs.push(
      room.addListener('connect', () => {
        set({ connection: 'connected' })
      })
    )

    unsubs.push(
      room.addListener('disconnect', () => {
        const activeRoom = get().room
        if (activeRoom === room) {
          set({ connection: 'disconnected' })
        }
      })
    )

    unsubs.push(
      room.addListener('message', (msg) => {
        const attrs = extractChatAttrs(msg)

        const username =
          typeof attrs.username === 'string' && attrs.username.trim()
            ? attrs.username.trim()
            : undefined

        const avatar_url =
          typeof attrs.avatar === 'string' && attrs.avatar.trim()
            ? attrs.avatar.trim()
            : undefined

        const to_user =
          attrs?.to_user !== undefined && attrs?.to_user !== null
            ? String(attrs.to_user)
            : null

        const ui: UIMessage = {
          ...msg,
          username,
          avatar_url,
          to_user,
          isOwner:
            String(msg.sender?.userId ?? '') === String(streamOwnerId ?? ''),
          sendTime: msg.sendTime ?? new Date(),
        }

        set(s => {
          const current = s.messagesByStream[nextStreamKey] ?? []

          if (ui.id && current.some(existingMsg => existingMsg.id === ui.id)) {
            return s
          }

          return {
            messagesByStream: {
              ...s.messagesByStream,
              [nextStreamKey]: [...current, ui],
            },
          }
        })

        if (archiver && devTap && roomArn) {
          const id = msg.id ? String(msg.id) : ''
          const tapKey = `${nextStreamKey}:${id}`

          if (id && tappedIds.has(tapKey)) return
          if (id) tappedIds.add(tapKey)

          void devTap({
            roomArn,
            messageId: id || null,
            userId: String(msg.sender?.userId ?? 'unknown'),
            username: username ?? String(userData?.username ?? ''),
            content: msg.content ?? '',
            ts: Date.now(),
            attrs,
            avatar_url: avatar_url ?? getAvatarUrl(userData) ?? undefined,
            to_user: to_user ?? null,
          })
        }
      })
    )

    ;(room as any).__unsubs = unsubs

    set({
      room,
      activeStreamId: nextStreamKey || null,
    })

    try {
      room.connect()
    } catch (e) {
      console.error('[IVS] connect error', e)

      if (get().room === room) {
        set({
          room: null,
          connection: 'disconnected',
        })
      }
    }
  },

  disconnectChat: () => {
    const room = get().room
    teardownRoom(room)

    set({
      room: null,
      connection: 'disconnected',
      activeStreamId: null,
      streamOwnerId: null,
      userData: undefined,
    })
  },

  sendMessage: async (content: string, options?: SendMessageOptions) => {
    const trimmed = content.trim()
    if (!trimmed) return
    if (get().isSending) return

    const user = get().userData
    const avatarUrl = getAvatarUrl(user)
    const streamKey = String(get().activeStreamId ?? '')
    const streamOwnerId = get().streamOwnerId
    set({ isSending: true })

    try {
      // PRIVATE MESSAGE -> backend only
      if (options?.toUser) {
        const response = await sendPrivateLiveChatMessage({
          stream_id: streamKey || null,
          to_user: options.toUser,
          content: trimmed,
        })

        const msg = response?.item

        if (msg && streamKey) {
          const privateMessage: UIMessage = {
            id: String(msg.id ?? `private-${Date.now()}`),
            content: msg.content ?? trimmed,
            sender: {
              userId: String(msg.user_id ?? user?.id ?? ''),
            },
            username: msg.username ?? user?.username ?? '',
            avatar_url: msg.avatar_url ?? avatarUrl,
            to_user: msg.to_user != null ? String(msg.to_user) : null,
            sendTime: msg.created_at ? new Date(msg.created_at) : new Date(),
            isOwner: String(msg.user_id ?? user?.id ?? '') === String(streamOwnerId ?? ''),
            requestId: undefined,
          } satisfies UIMessage

          set(s => {
            const current = s.messagesByStream[streamKey] ?? []

            if (current.some(existingMsg => existingMsg.id === privateMessage.id)) {
              return s
            }

            return {
              messagesByStream: {
                ...s.messagesByStream,
                [streamKey]: [...current, privateMessage],
              },
            }
          })
        }

        return
      }

      // PUBLIC MESSAGE -> IVS
      const room = get().room
      if (!room) return
      if (get().connection !== 'connected') return

      await room.sendMessage(
        new SendMessageRequest(trimmed, {
          attributes: JSON.stringify({
            username: user?.username ?? '',
            avatar: avatarUrl,
            to_user: null,
          }),
        })
      )
    } catch (e) {
      console.error('[LIVECHAT] send error', e)
    } finally {
      set({ isSending: false })
    }
  },
  clearMessagesForStream: (streamId) =>
    set(s => {
      const streamKey = String(streamId)
      return {
        messagesByStream: {
          ...s.messagesByStream,
          [streamKey]: [],
        },
      }
    }),

  getMessagesForStream: (streamId) => {
    const streamKey = String(streamId ?? '')
    return get().messagesByStream[streamKey] ?? []
  },

  markHistoryLoadedForStream: (streamId) =>
    set(s => {
      const streamKey = String(streamId ?? '')
      return {
        historyLoadedByStream: {
          ...s.historyLoadedByStream,
          [streamKey]: true,
        },
      }
    }),

  clearHistoryLoadedForStream: (streamId) =>
    set(s => {
      const streamKey = String(streamId ?? '')
      const next = { ...s.historyLoadedByStream }
      delete next[streamKey]

      return {
        historyLoadedByStream: next,
      }
    }),

  hasHistoryLoadedForStream: (streamId) => {
    const streamKey = String(streamId ?? '')
    return get().historyLoadedByStream[streamKey] ?? false
  },

  prependHistoryForStream: (streamId, rows, streamOwnerId) =>
    set(s => {
      const streamKey = String(streamId ?? '')
      const current = s.messagesByStream[streamKey] ?? []

      const normalizedRows = Array.isArray(rows)
        ? rows.map(row => normalizeHistoryMessage(row, streamOwnerId))
        : []

      if (!normalizedRows.length) {
        return s
      }

      const existingIds = new Set(
        current
          .map(message => message.id)
          .filter(Boolean)
          .map(id => String(id))
      )

      const dedupedHistory = normalizedRows.filter(message => {
        if (!message.id) return true
        return !existingIds.has(String(message.id))
      })

      if (!dedupedHistory.length) {
        return s
      }

      return {
        messagesByStream: {
          ...s.messagesByStream,
          [streamKey]: [...dedupedHistory, ...current],
        },
      }
    }),
}))